import { motion, AnimatePresence } from "framer-motion";
import { AlertTriangle, X, Phone, Mail } from "lucide-react";

export type DupLead = {
  id: string;
  name: string;
  phone: string | null;
  email: string | null;
  status: string;
  source?: string | null;
  created_at?: string;
};

interface DuplicateWarningModalProps {
  /** Si tiene elementos, se muestra el modal. */
  duplicates: DupLead[];
  /** Cancelar el guardado (cerrar, backdrop, botón Cancelar). */
  onCancel: () => void;
  /** "Crear de todas formas": guardar el contacto a pesar del duplicado. */
  onConfirm: () => void;
  /** Opcional: abrir/ver el contacto existente. Si se omite, no se muestra el botón "Ver". */
  onView?: (id: string) => void;
}

/**
 * Modal de advertencia de posibles contactos duplicados (mismo teléfono o email).
 * Avisa, no bloquea: permite Cancelar o "Crear de todas formas".
 *
 * Es autocontenido (trae su propio <style> con clases `dwm-*`) para que se vea igual
 * en cualquier pantalla sin depender del CSS de modal inline de cada página. Reutiliza
 * solo los botones globales (btn-primary / btn-secondary / btn-icon-sm) de app.css.
 */
export function DuplicateWarningModal({
  duplicates,
  onCancel,
  onConfirm,
  onView,
}: DuplicateWarningModalProps) {
  return (
    <AnimatePresence>
      {duplicates.length > 0 && (
        <div className="dwm-backdrop" onClick={onCancel}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="dwm-panel"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="dwm-header">
              <div className="dwm-title">
                <AlertTriangle size={18} color="#f59e0b" />
                <h2 className="text-h2">Posible duplicado detectado</h2>
              </div>
              <button className="btn-icon-sm" onClick={onCancel} aria-label="Cerrar">
                <X size={18} />
              </button>
            </div>
            <p className="dwm-desc">
              Se encontraron contactos con el mismo teléfono o email:
            </p>
            <div className="dwm-list">
              {duplicates.map((d) => (
                <div key={d.id} className="dwm-item">
                  <div className="dwm-item-info">
                    <span className="dwm-name">{d.name}</span>
                    {d.phone && (
                      <span className="dwm-field">
                        <Phone size={12} /> {d.phone}
                      </span>
                    )}
                    {d.email && (
                      <span className="dwm-field">
                        <Mail size={12} /> {d.email}
                      </span>
                    )}
                    <span className="dwm-status">{d.status}</span>
                  </div>
                  {onView && (
                    <button
                      className="btn-secondary dwm-view-btn"
                      onClick={() => onView(d.id)}
                    >
                      Ver
                    </button>
                  )}
                </div>
              ))}
            </div>
            <div className="dwm-actions">
              <button className="btn-secondary" onClick={onCancel}>
                Cancelar
              </button>
              <button className="btn-primary" onClick={onConfirm}>
                Crear de todas formas
              </button>
            </div>
          </motion.div>

          <style>{`
            .dwm-backdrop {
              position: fixed; inset: 0;
              background: rgba(33, 30, 25, 0.45);
              backdrop-filter: blur(3px);
              display: flex; align-items: center; justify-content: center;
              z-index: 1100; padding: 16px;
            }
            .dwm-panel {
              background: var(--surface);
              border: 1px solid var(--border);
              border-radius: var(--radius-xl);
              box-shadow: var(--shadow-pop);
              padding: 28px;
              width: 100%; max-width: 520px;
              max-height: 90vh; overflow-y: auto;
            }
            .dwm-header {
              display: flex; align-items: center; justify-content: space-between;
              gap: 12px; margin-bottom: 16px;
            }
            .dwm-title { display: flex; align-items: center; gap: 8px; }
            .dwm-title h2 { margin: 0; }
            .dwm-desc {
              font-size: var(--text-sm); color: var(--text-muted); margin: 0 0 16px;
            }
            .dwm-list {
              display: flex; flex-direction: column; gap: 8px;
              margin-bottom: 20px; max-height: 280px; overflow-y: auto;
            }
            .dwm-item {
              display: flex; align-items: center; justify-content: space-between; gap: 12px;
              padding: 10px 12px; background: var(--neutral-100);
              border-radius: var(--radius-md); border: 1px solid var(--border);
            }
            .dwm-item-info { display: flex; flex-direction: column; gap: 3px; flex: 1; min-width: 0; }
            .dwm-name { font-weight: 600; font-size: var(--text-sm); color: var(--text-strong); }
            .dwm-field { display: flex; align-items: center; gap: 4px; font-size: var(--text-label); color: var(--text-muted); }
            .dwm-status { font-size: var(--text-label); color: var(--interactive); font-weight: 500; }
            .dwm-view-btn { padding: 5px 12px; font-size: var(--text-sm); flex-shrink: 0; }
            .dwm-actions { display: flex; justify-content: flex-end; gap: 8px; }
          `}</style>
        </div>
      )}
    </AnimatePresence>
  );
}
